import { organisationDetailsFixture, type OrganisationDetails, type OrganisationDetailsState, type OrganisationFieldError, validateOrganisationDetails } from './organisationDetailsModel';
import { fetchOrganisationById, updateOrganisation } from './organisationHttpClient';
import type { OrgDetailsDto, UpdateOrgRequestDto } from './organisationApiTypes';

export type OrganisationScenario = 'default' | 'permissionDenied';

export interface SaveOrganisationResult {
  ok: boolean;
  errors?: OrganisationFieldError;
}

const mapDtoToOrganisationDetails = (dto: OrgDetailsDto): OrganisationDetails => ({
  organisationId: dto.id ?? '',
  tenantId: '',
  legalName: dto.legalName ?? '',
  tradingName: dto.tradingName ?? '',
  abn: dto.abn ?? '',
  acn: dto.acn ?? '',
  isStpReporter: dto.isStpReporter,
  regionCode: dto.regionCode ?? '',
  defaultCurrency: dto.defaultCurrency ?? '',
  defaultTimezone: dto.defaultTimezone ?? '',
  primaryContact: {
    name: dto.primaryContact?.name ?? '',
    role: dto.primaryContact?.role ?? '',
    email: dto.primaryContact?.email ?? '',
    phone: dto.primaryContact?.phone ?? '',
  },
  registeredBusinessAddress: {
    street: dto.registeredBusinessAddress?.street ?? '',
    suburb: dto.registeredBusinessAddress?.suburb ?? '',
    state: dto.registeredBusinessAddress?.state ?? '',
    postcode: dto.registeredBusinessAddress?.postcode ?? '',
    country: dto.registeredBusinessAddress?.country ?? '',
  },
  status: (dto.status as 'Active' | 'Terminated') ?? 'Active',
  effectiveAt: dto.effectiveFrom ?? '',
  retroactiveReason: dto.retroactiveReason ?? '',
  defaultLogoImageId: dto.branding?.defaultLogoImageId ?? '',
  brandPrimaryHex: dto.branding?.brandPrimaryHex ?? '',
  brandAccentHex: dto.branding?.brandAccentHex ?? '',
  brandSecondaryHex: dto.branding?.brandSecondaryHex ?? '',
  updatedAt: dto.updatedAt ?? '',
  updatedByUserId: dto.updatedBy ?? '',
});

const detailsToUpdateRequest = (details: OrganisationDetails): UpdateOrgRequestDto => ({
  id: details.organisationId,
  legalName: details.legalName,
  tradingName: details.tradingName,
  abn: details.abn || undefined,
  acn: details.acn || undefined,
  isStpReporter: details.isStpReporter,
  primaryContact: {
    name: details.primaryContact.name,
    role: details.primaryContact.role,
    email: details.primaryContact.email,
    phone: details.primaryContact.phone,
  },
  registeredBusinessAddress: {
    street: details.registeredBusinessAddress.street,
    suburb: details.registeredBusinessAddress.suburb,
    state: details.registeredBusinessAddress.state,
    postcode: details.registeredBusinessAddress.postcode,
    country: details.registeredBusinessAddress.country,
  },
  branding: {
    defaultLogoImageId: details.defaultLogoImageId || undefined,
    brandPrimaryHex: details.brandPrimaryHex || undefined,
    brandAccentHex: details.brandAccentHex || undefined,
    brandSecondaryHex: details.brandSecondaryHex || undefined,
  },
  effectiveFrom: details.effectiveAt || undefined,
  retroactiveReason: details.retroactiveReason || undefined,
});

export const loadOrganisationDetails = async (scenario: OrganisationScenario = 'default', orgId?: string): Promise<OrganisationDetailsState> => {
  if (scenario === 'permissionDenied')
    return {
      details: organisationDetailsFixture,
      permissions: { canEdit: false, canEditRestrictedStatus: false, canView: false },
    };

  if (orgId) {
    const response = await fetchOrganisationById(orgId);
    const details = response.result ? mapDtoToOrganisationDetails(response.result) : organisationDetailsFixture;
    return {
      details,
      permissions: { canEdit: true, canEditRestrictedStatus: false, canView: true },
    };
  }

  return {
    details: organisationDetailsFixture,
    permissions: { canEdit: true, canEditRestrictedStatus: false, canView: true },
  };
};

export const saveOrganisationDetails = async (details: OrganisationDetails): Promise<SaveOrganisationResult> => {
  const errors = validateOrganisationDetails(details);
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  try {
    await updateOrganisation(detailsToUpdateRequest(details));
    return { ok: true };
  } catch {
    return { ok: false };
  }
};
